import { useState } from "react";
import axios from "axios";
import {
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    Clock,
    FolderOpen,
    Monitor,
    User,
} from "lucide-react";
import CloudEntityActivityLogs from "./CloudEntityActivityLogs";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { useQuery } from "react-query";
import { formatTimeAgo } from "../formatTimeAgo";


const deriveAwsLogFields = (log: any) => {
    const raw = log?.rawLog || {};
    const userIdentity = raw.userIdentity || {};
    const requestParameters = raw.requestParameters || {};

    const derivedAction = raw.eventName
        ? `${raw.eventSource || ""}:${raw.eventName}`.replace(/^:/, "")
        : log.action;

    const clean = (val: any) => {
        if (!val) return "—";
        if (typeof val === "string" && val.toLowerCase() === "unknown") return "—";
        return val;
    };

    const resourceFromResources = Array.isArray(raw.resources) && raw.resources.length > 0
        ? (raw.resources[0].ARN || raw.resources[0].arn || raw.resources[0].resourceName || raw.resources[0])
        : null;

    const resourceFromRequest =
        requestParameters.bucketName ||
        requestParameters.queueUrl ||
        requestParameters.topicArn ||
        requestParameters.functionName ||
        requestParameters.tableName ||
        requestParameters.streamName ||
        requestParameters.logGroupName ||
        requestParameters.resourceArn ||
        requestParameters.resourceName ||
        requestParameters.resourceId ||
        requestParameters.distributionId ||
        requestParameters.dbClusterIdentifier ||
        requestParameters.dbInstanceIdentifier ||
        requestParameters.userName ||
        requestParameters.roleName ||
        requestParameters.policyArn ||
        requestParameters.groupName ||
        requestParameters.targetGroupArn ||
        requestParameters.certificateArn ||
        requestParameters.secretId ||
        requestParameters.secretArn ||
        requestParameters.keyId ||
        requestParameters.keyArn ||
        requestParameters.alarmName ||
        requestParameters.alarmArn ||
        requestParameters.identityPoolId ||
        requestParameters.trailARN ||
        requestParameters.trailName;

    const resourceDisplay = clean(
        resourceFromRequest ||
        (resourceFromResources === userIdentity.arn ? null : resourceFromResources) ||
        raw.eventSource ||
        log.cloudResourceType ||
        log.entityAssociated ||
        log.entity
    );

    return {
        action: clean(derivedAction || log.action),
        resourceDisplay,
        entityDisplay: clean(userIdentity.userName || userIdentity.type || userIdentity.arn),
        sourceIp: clean(raw.sourceIPAddress || (Array.isArray(log.sourceIp) ? log.sourceIp[0] : log.sourceIp)),
        accountId: clean(userIdentity.accountId || log.cloudId),
        timestamp: clean(raw.eventTime || log.logTimestamp),
        service: clean(raw.eventSource || log.cloudResourceType || "AWS"),
    };
};


export default function CloudRecentActivity({
    url,
    tenantName,
    cloudType,
}: {
    url: string;
    tenantName: string;
    cloudType: string;
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedAlerts, setExpandedAlerts] = useState<Set<number>>(new Set());
    const [expandedTags, setExpandedTags] = useState<Set<number>>(new Set());
    const itemsPerPage = 5;

    const {
        data: activity = [],
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["activity", tenantName, cloudType],
        queryFn: async () => {
            const { data } = await axios.get(url);
            return Array.isArray(data) ? data : [];
        },
    });

    const totalPages = Math.ceil(activity.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedLogs = activity.slice(startIndex, startIndex + itemsPerPage);

    const getSeverityBadge = (level: string = "info") => {
        switch (level?.toLowerCase()) {
            case "info":
                return (
                    <Badge className="text-xs text-white bg-blue-500 hover:bg-blue-600">
                        {level}
                    </Badge>
                );
            case "notice":
                return (
                    <Badge className="text-xs text-white bg-orange-500 hover:bg-orange-600">
                        High
                    </Badge>
                );
            case "error":
                return (
                    <Badge variant={"destructive"}>
                        {level}
                    </Badge>
                );
            case "warn":
                return (
                    <Badge className="text-xs text-white bg-yellow-500 hover:bg-yellow-600">
                        {level}
                    </Badge>
                );
            case "low":
                return (
                    <Badge className="text-xs text-white bg-blue-500 hover:bg-blue-600">
                        Low
                    </Badge>
                );
            default:
                return (
                    <Badge variant="secondary" className="text-xs">
                        INFO
                    </Badge>
                );
        }
    };

    const toggleAlertExpansion = (id: number) => {
        const newExpanded = new Set(expandedAlerts);
        newExpanded.has(id) ? newExpanded.delete(id) : newExpanded.add(id);
        setExpandedAlerts(newExpanded);
    };

    const toggleTagsExpansion = (id: number) => {
        const newExpanded = new Set(expandedTags);
        newExpanded.has(id) ? newExpanded.delete(id) : newExpanded.add(id);
        setExpandedTags(newExpanded);
    };


    const getPageNumbers = (currentPage: number, totalPages: number) => {
        const delta = 2;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let last: number | null = null;
        for (let i = 1; i <= totalPages; i++) {
            if (
                i === 1 ||
                i === totalPages ||
                (i >= currentPage - delta && i <= currentPage + delta)
            ) {
                range.push(i);
            }
        }
        for (let i of range) {
            if (last) {
                if ((i as number) - last === 2) rangeWithDots.push(last + 1);
                else if ((i as number) - last !== 1) rangeWithDots.push("…");
            }
            rangeWithDots.push(i);
            last = i as number;
        }
        return rangeWithDots;
    };

    const renderSkeletonRow = (key: number) => (
        <div
            key={key}
            className="grid gap-4 px-3 py-3 text-sm animate-pulse"
            style={{ gridTemplateColumns: "100px 2fr 2fr 1fr" }}
        >
            <div className="w-20 h-6 bg-gray-300 rounded" />
            <div className="w-full h-6 bg-gray-300 rounded" />
            <div className="w-full h-6 bg-gray-300 rounded" />
            <div className="w-16 h-6 bg-gray-300 rounded" />
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>Recent Cloud Activities</CardTitle>
            </CardHeader>

            <CardContent>
                <div className="space-y-4">
                    <div
                        className="grid gap-4 pb-2 text-[11px] font-semibold tracking-wide uppercase border-b text-muted-foreground"
                        style={{
                            gridTemplateColumns: "90px 2fr 1.3fr 1.3fr 1fr 1fr",
                        }}
                    >
                        <div>Level</div>
                        <div>Details</div>
                        <div>Action</div>
                        <div>Resource</div>
                        <div>Project</div>
                        <div>Source IP</div>
                    </div>
                    {isLoading && (
                        <div className="space-y-1">
                            {[...Array(itemsPerPage)].map((_, idx) => renderSkeletonRow(idx))}
                        </div>
                    )}
                    {!isLoading && activity.length === 0 && (
                        <div className="py-8 text-sm text-center text-muted-foreground">
                            No activity logs found.
                        </div>
                    )}


                    {!isLoading &&
                        paginatedLogs.map((log: any) => {
                            const isExpanded = expandedAlerts.has(log.id);
                            const isAWS = (log.cloudType || "").toLowerCase() === "aws";
                            const awsDerived = isAWS ? deriveAwsLogFields(log) : null;

                            return (
                                <div
                                    key={log.id}
                                    className="border rounded-md border-border/50 hover-elevate transition hover:scale-[1.01]"
                                >
                                    <div
                                        className="grid gap-4 px-3 py-3 text-sm cursor-pointer"
                                        style={{
                                            gridTemplateColumns: "90px 2fr 1.3fr 1.3fr 1fr 1fr",
                                        }}
                                        onClick={() => toggleAlertExpansion(log.id)}
                                    >
                                        {/* Level Section */}
                                        <div className="flex items-center gap-2">
                                            {isExpanded ? (
                                                <ChevronUp className="w-4 h-4" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4" />
                                            )}
                                            {getSeverityBadge(log.logType)}
                                        </div>

                                        {/* Details Section */}
                                        <div className="min-w-0 space-y-1">
                                            <div className="flex items-center gap-1 text-xs text-foreground">
                                                <Monitor className="w-3 h-3 text-muted-foreground" />
                                                <span className="truncate" title={isAWS ? awsDerived?.service : log.entity}>
                                                    {isAWS ? awsDerived?.service : log.entity}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="w-3 h-3" />
                                                <span>{formatTimeAgo(isAWS ? awsDerived?.timestamp : log.logTimestamp)}</span>
                                            </div>
                                        </div>

                                        {/* Action Section */}
                                        <div className="space-y-1">

                                            <div className="font-semibold text-foreground text-xs text-muted-foreground break-all whitespace-pre-wrap" title={isAWS ? awsDerived?.action : log.action}>
                                                {isAWS ? awsDerived?.action : log.action}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground" title={log.cloudResourceType}>
                                                {log.cloudResourceType}
                                            </div>
                                        </div>

                                        {/* Resource Section */}
                                        <div>
                                            <div className="text-xs text-foreground" title={isAWS ? awsDerived?.resourceDisplay : log.entityAssociated || "—"}>
                                                {isAWS ? awsDerived?.resourceDisplay : log.entityAssociated || "—"}
                                            </div>
                                        </div>

                                        {/* Project Section */}
                                        <div>
                                            <Badge className="bg-gray-500 text-white text-xs px-2" title={isAWS ? awsDerived?.accountId : log.cloudId}>
                                                {isAWS ? awsDerived?.accountId : log.cloudId}
                                            </Badge>
                                        </div>

                                        {/* Source IP Section */}
                                        <div>
                                            <div className="text-xs text-foreground" title={(isAWS ? awsDerived?.sourceIp : log.sourceIp) || "—"}>
                                                {(isAWS ? awsDerived?.sourceIp : log.sourceIp) || "—"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Tree Viewer */}
                                    {isExpanded && (
                                        <CloudEntityActivityLogs data={log.rawLog} />
                                    )}
                                </div>

                            );
                        })}
                    {!isLoading && activity.length > 0 && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                {getPageNumbers(currentPage, totalPages).map((p, i) =>
                                    typeof p === "number" ? (
                                        <Button
                                            key={i}
                                            variant={currentPage === p ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setCurrentPage(p)}
                                            className="h-8 min-w-8"
                                        >
                                            {p}
                                        </Button>
                                    ) : (
                                        <span key={i} className="px-2 text-muted-foreground">
                                            …
                                        </span>
                                    )
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>


    );
}
